import React, { useEffect, useMemo, useState } from "react";
import { FitList, TitleWithMore, WidgetShell, EmptyState, useCoordinates } from "./common";
import { useStormData } from "../storm/useStormData";
import { isPreviewStill } from "../boardData";
import {
  REPORT_STYLE,
  alertStyle,
  clockTime,
  compass,
  filterReports,
  relTime,
  reportKind,
  reportMagnitude,
  reportSource,
  sortAlerts,
  sortCells,
  threatOf,
} from "../storm/stormUi";

// Shared plumbing for the Storm Center side panels: resolve the station, read
// the shared storm feed, and cover the no-location / loading / failed states.
function useStormPanel(agency, board, config) {
  const station = useCoordinates(agency, board);
  const radiusMi = Number(config.radiusMi) || 75;
  const feed = useStormData({ agency, lat: station.lat, lng: station.lng, radiusMi });
  return { station, radiusMi, ...feed };
}

function panelFallback({ station, data, loading, error }) {
  if (station.lat == null || station.lng == null) {
    return <EmptyState icon="📍" label="Set this board's location to track storms." />;
  }
  if (!data && error) return <EmptyState icon="⚠" label="Storm data is unavailable right now." />;
  // Never claim "all clear" before the feed has actually answered.
  if (!data) return <div className="sb-loading-text">Loading storm data…</div>;
  return null;
}

// ---- Storm cells ------------------------------------------------------------
// Every tracked NEXRAD cell in range, headed-for-the-station ones first, with
// what makes it dangerous (TVS, mesocyclone, hail size) and when it arrives.
export const StormCellsWidget = ({ config = {}, agency, board }) => {
  const panel = useStormPanel(agency, board, config);
  const limit = Number(config.limit) || 6;
  const cells = useMemo(
    () => sortCells(panel.data?.cells, { approachingFirst: config.approachingFirst !== false }).slice(0, limit),
    [panel.data, config.approachingFirst, limit]
  );
  const fallback = panelFallback(panel);
  const [more, setMore] = useState(0);
  const feedDown = panel.data && panel.data.sources?.nexrad?.ok === false;

  return (
    <WidgetShell title={<TitleWithMore title="Storm Cells" more={cells.length ? more : 0} />} accent="#f59e0b" className="sb-storm-panel">
      {fallback ||
        (feedDown ? (
          <EmptyState icon="⚠" label="Radar cell data is unavailable right now." />
        ) : cells.length === 0 ? (
          <div className="sb-storm-clear">
            <b>All clear</b>
            <span>No storm cells within {panel.radiusMi} mi</span>
          </div>
        ) : (
          <FitList className="sb-storm-list" onHiddenChange={setMore} itemsKey={cells.map((c) => c.id).join("|")}>
            {cells.map((c) => {
              const t = threatOf(c.threat);
              const hazards = [
                c.tvs && "TVS",
                c.meso && "Rotation",
                c.hailIn ? `${c.hailIn}″ hail` : null,
                c.topKft ? `Tops ${Math.round(c.topKft)}k ft` : null,
              ].filter(Boolean);
              return (
                <div key={c.id} className={`sb-storm-row sb-fit-item${c.approaching ? " approaching" : ""}`} style={{ "--c": t.color }}>
                  <span className="sb-storm-badge">{t.label}</span>
                  <div className="sb-storm-row-main">
                    <div className="sb-storm-row-title">
                      {c.stormId || "Cell"} <small>{c.nexrad}</small>
                    </div>
                    <div className="sb-storm-row-sub">
                      {Math.round(c.distanceMi)} mi {compass(c.bearingFromStation)} · moving {compass(c.headingDeg)}{" "}
                      {Math.round(c.speedMph || 0)} mph
                      {hazards.length ? ` · ${hazards.join(" · ")}` : ""}
                    </div>
                  </div>
                  {c.approaching && c.etaMin != null && (
                    <span className="sb-storm-eta">
                      ETA <b>{Math.round(c.etaMin)}</b> min
                    </span>
                  )}
                </div>
              );
            })}
          </FitList>
        ))}
    </WidgetShell>
  );
};

// ---- Storm reports ----------------------------------------------------------
// Ground truth: NWS spotter/local storm reports, responders' hazard reports
// from the Ladder app (with their comment), plus mPING crowd reports when
// Ladder's platform mPING key is set. Newest first.
export const StormReportsWidget = ({ config = {}, agency, board }) => {
  const panel = useStormPanel(agency, board, config);
  const limit = Number(config.limit) || 8;
  const sources = config.sources || "all";
  const reports = useMemo(
    () =>
      filterReports(panel.data?.reports, { hours: config.hours || 3, sources })
        .sort((a, b) => (b.timeMs || 0) - (a.timeMs || 0))
        .slice(0, limit),
    [panel.data, config.hours, sources, limit]
  );
  const fallback = panelFallback(panel);
  const [more, setMore] = useState(0);
  const mpingOff = sources === "mping" && panel.data && !panel.data.sources?.mping?.configured;

  return (
    <WidgetShell title={<TitleWithMore title="Storm Reports" more={reports.length ? more : 0} />} accent="#22d3ee" className="sb-storm-panel">
      {fallback ||
        (mpingOff ? (
          <EmptyState icon="ⓘ" label="mPING reports are not enabled." />
        ) : reports.length === 0 ? (
          <div className="sb-storm-clear">
            <b>No reports</b>
            <span>
              Nothing reported within {panel.radiusMi} mi in the last {config.hours || 3}h
            </span>
          </div>
        ) : (
          <FitList className="sb-storm-list" onHiddenChange={setMore} itemsKey={reports.map((r) => r.id).join("|")}>
            {reports.map((r) => {
              const s = REPORT_STYLE[reportKind(r.type)];
              const mag = reportMagnitude(r);
              return (
                <div key={r.id} className={`sb-storm-row sb-fit-item${r.source === "ladder" ? " ladder" : ""}`} style={{ "--c": s.color }}>
                  <span className="sb-storm-glyph">{s.glyph}</span>
                  <div className="sb-storm-row-main">
                    <div className="sb-storm-row-title">
                      {r.type}
                      {mag ? ` ${mag}` : ""}
                    </div>
                    <div className="sb-storm-row-sub">
                      {[[r.city, r.state].filter(Boolean).join(", ") || (r.county ? `${r.county} Co.` : ""),
                        r.distanceMi != null ? `${Math.round(r.distanceMi)} mi` : "",
                        reportSource(r.source),
                        r.source === "ladder" && r.remark ? `“${r.remark}”` : ""]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <span className="sb-storm-time">{relTime(r.timeMs)}</span>
                </div>
              );
            })}
          </FitList>
        ))}
    </WidgetShell>
  );
};

// ---- NWS alerts -------------------------------------------------------------
// Active NWS alerts in range (warnings, watches, advisories, statements), the
// ones covering the station itself on top, colored with NWS's own map colors.
// When they don't all fit, the alerts rotate at full text size: a page of
// as many as fit, then the next, every `rotateSeconds` (config.rotate: false shows the first
// `limit` and "+N more" instead). The station's own alerts sort first, so
// they lead page one.
const ALERT_ROTATE_MAX = 30;

export const NwsAlertsWidget = ({ config = {}, agency, board }) => {
  const panel = useStormPanel(agency, board, config);
  const rotate = config.rotate !== false;
  const limit = rotate ? ALERT_ROTATE_MAX : Number(config.limit) || 5;
  const alerts = useMemo(() => {
    const list = sortAlerts(panel.data?.alerts);
    return (config.stationOnly ? list.filter((a) => a.affectsStation) : list).slice(0, limit);
  }, [panel.data, config.stationOnly, limit]);
  const fallback = panelFallback(panel);
  const [more, setMore] = useState(0);
  const feedDown = panel.data && panel.data.sources?.nws?.ok === false;

  // Rotation: `offset` is the first alert on the current page; the page is
  // whatever of alerts[offset..] fits (FitList reports the rest as hidden).
  const [offset, setOffset] = useState(0);
  const start = offset < alerts.length ? offset : 0;
  const page = rotate ? alerts.slice(start) : alerts;
  const onPage = Math.max(1, page.length - more);
  const paging = rotate && alerts.length > onPage;
  const seconds = Math.max(4, Number(config.rotateSeconds) || 8);
  useEffect(() => {
    if (!paging || isPreviewStill()) return undefined;
    const t = setTimeout(() => setOffset(start + onPage >= alerts.length ? 0 : start + onPage), seconds * 1000);
    return () => clearTimeout(t);
  }, [paging, start, onPage, alerts.length, seconds]);
  const last = Math.min(alerts.length, start + onPage);
  const label = paging ? `${last > start + 1 ? `${start + 1}–${last}` : last} of ${alerts.length}` : null;

  return (
    <WidgetShell
      title={<TitleWithMore title="NWS Warnings" more={alerts.length ? more : 0} label={label} />}
      accent="#f87171"
      className="sb-storm-panel"
    >
      {fallback ||
        (feedDown ? (
          <EmptyState icon="⚠" label="NWS alerts are unavailable right now." />
        ) : alerts.length === 0 ? (
          <div className="sb-storm-clear">
            <b>No active NWS alerts</b>
            <span>{config.stationOnly ? "for the station" : `within ${panel.radiusMi} mi`}</span>
          </div>
        ) : (
          <FitList
            key={rotate ? `page-${start}` : "all"}
            className={`sb-storm-list${paging ? " rotating" : ""}`}
            min={rotate ? 1 : undefined}
            onHiddenChange={setMore}
            itemsKey={page.map((a) => a.id).join("|")}
          >
            {page.map((a) => (
              <div
                key={a.id}
                className={`sb-storm-alert sb-fit-item${a.affectsStation ? " here" : ""}`}
                style={{ "--c": alertStyle(a.event).color }}
              >
                <div className="sb-storm-row-title">
                  {a.event}
                  {a.affectsStation && <span className="sb-storm-here">Includes the station</span>}
                </div>
                <div className="sb-storm-row-sub">
                  {a.expiresMs ? `Until ${clockTime(a.expiresMs)}` : ""}
                  {a.areaDesc ? `${a.expiresMs ? " · " : ""}${a.areaDesc}` : ""}
                </div>
              </div>
            ))}
          </FitList>
        ))}
    </WidgetShell>
  );
};
